import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Platforms with an API adapter. */
export const API_PLATFORMS = ["facebook", "instagram", "telegram", "gbp", "linkedin", "tiktok"] as const;
/** Platforms worked by hand: a card to copy and a person who marks it published. */
export const MANUAL_PLATFORMS = ["threads", "youtube", "pinterest", "bluesky", "mastodon", "x", "other"] as const;
export const CHANNEL_PLATFORMS = [...API_PLATFORMS, ...MANUAL_PLATFORMS] as const;
export const ChannelPlatform = z.enum(CHANNEL_PLATFORMS);
export type ChannelPlatform = z.infer<typeof ChannelPlatform>;

/** manual: a card only. semi: goes out through the API when a person triggers it. auto: the schedule sends it. */
export const CHANNEL_MODES = ["manual", "semi", "auto"] as const;
export const ChannelMode = z.enum(CHANNEL_MODES);
export type ChannelMode = z.infer<typeof ChannelMode>;

export const CHANNEL_STATUSES = ["pending", "needs_target", "active", "degraded", "expired", "revoked", "error", "disconnected"] as const;
export const ChannelStatus = z.enum(CHANNEL_STATUSES);
export type ChannelStatus = z.infer<typeof ChannelStatus>;

export const SOCIAL_POST_STATUSES = ["queued", "publishing", "retrying", "manual_pending", "published", "failed", "needs_review", "withdrawn"] as const;
export const SocialPostStatus = z.enum(SOCIAL_POST_STATUSES);
export type SocialPostStatus = z.infer<typeof SocialPostStatus>;

export const NOTICE_SEVERITIES = ["info", "warning", "critical"] as const;
export const NoticeSeverity = z.enum(NOTICE_SEVERITIES);
export type NoticeSeverity = z.infer<typeof NoticeSeverity>;

export const NOTICE_CODES = [
  "token_expired",
  "token_expiring",
  "token_revoked",
  "permission_missing",
  "page_disconnected",
  "channel_error",
  "publish_unconfirmed",
  "publish_partial",
  "publish_failed",
  "first_comment_not_sent",
  "withdraw_manual",
  "manual_reminder",
  "no_targets",
] as const;
export const NoticeCode = z.enum(NOTICE_CODES);
export type NoticeCode = z.infer<typeof NoticeCode>;

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

/** Non-secret settings. Tokens never travel in here: the service refuses a key that looks like a credential. */
export const ChannelConfig = z.record(z.string().max(64), z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]));
export type ChannelConfig = z.infer<typeof ChannelConfig>;

export const ChannelCreate = z.object({
  platform: ChannelPlatform,
  name: z.string().trim().min(1).max(200),
  handle: z.string().trim().max(200).nullish(),
  config: ChannelConfig.optional(),
});
export type ChannelCreate = z.infer<typeof ChannelCreate>;

export const ChannelUpdate = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  handle: z.string().trim().max(200).nullish(),
  mode: ChannelMode.optional(),
  config: ChannelConfig.optional(),
  isActive: z.boolean().optional(),
});
export type ChannelUpdate = z.infer<typeof ChannelUpdate>;

export const ChannelDto = z.object({
  id: Uuid,
  platform: z.string(),
  mode: z.string(),
  status: z.string(),
  name: z.string(),
  handle: z.string().nullable(),
  externalId: z.string().nullable(),
  config: ChannelConfig,
  /** Whether a token is held. The token itself never leaves the service. */
  connected: z.boolean(),
  apiAvailable: z.boolean(),
  scopes: z.array(z.string()),
  tokenExpiresAt: IsoDateTime.nullable(),
  degradedFromMode: z.string().nullable(),
  lastHealthAt: IsoDateTime.nullable(),
  lastHealth: z.unknown().nullable(),
  lastError: z.string().nullable(),
  lastPublishedAt: IsoDateTime.nullable(),
  isActive: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ChannelDto = z.infer<typeof ChannelDto>;
export const ChannelPage = pageOf(ChannelDto);

export const ChannelListQuery = IdPageQuery.extend({ platform: ChannelPlatform.optional() });

export const TelegramTokenInput = z.object({
  /** Written once, encrypted at once, never returned. */
  botToken: z.string().trim().min(20).max(200),
  chatId: z.string().trim().min(1).max(200),
  channelUsername: z.string().trim().max(200).nullish(),
});

export const OAuthStartDto = z.object({ authorizeUrl: z.string().url() });

export const OAuthTargetDto = z.object({
  externalId: z.string(),
  name: z.string(),
  handle: z.string().nullable(),
  kind: z.string(),
});
export const OAuthTargetList = z.object({ items: z.array(OAuthTargetDto) });
export const SelectTargetInput = z.object({ externalId: z.string().min(1).max(500) });

export const ChannelHealthDto = z.object({
  status: z.string(),
  error: z.string().nullable(),
  expiresAt: IsoDateTime.nullable(),
  missingScopes: z.array(z.string()),
  targetMissing: z.boolean(),
  details: z.unknown().nullable(),
  checkedAt: IsoDateTime,
});
export type ChannelHealthDto = z.infer<typeof ChannelHealthDto>;

// ---------------------------------------------------------------------------
// Posts and delivery
// ---------------------------------------------------------------------------

export const SocialMedia = z.object({
  assetId: z.string().max(100).nullish(),
  url: z.string().url().max(2000),
  kind: z.enum(["image", "video", "document"]),
  mimeType: z.string().max(100),
  width: z.number().int().positive().nullish(),
  height: z.number().int().positive().nullish(),
  durationMs: z.number().int().positive().nullish(),
  altText: z.string().max(4000).nullish(),
  title: z.string().max(300).nullish(),
  sizeBytes: z.number().int().positive().nullish(),
});
export type SocialMedia = z.infer<typeof SocialMedia>;

/** LinkedIn's own composer stops a comment here. */
export const FIRST_COMMENT_MAX = 1250;
/** The one platform where a link goes in the first comment (docs/plan.md, the channel rules). */
export function takesFirstComment(platform: string): boolean {
  return platform === "linkedin";
}
export const FIRST_COMMENT_STATUSES = ["sent", "not_sent", "by_hand", "dry_run"] as const;
export const FirstCommentStatus = z.enum(FIRST_COMMENT_STATUSES);
export type FirstCommentStatus = z.infer<typeof FirstCommentStatus>;

export const SocialPostCreate = z.object({
  channelId: Uuid,
  body: z.string().max(70_000),
  hashtags: z.array(z.string().max(200)).max(100).default([]),
  linkUrl: z.string().url().max(2000).nullish(),
  /** Only where `takesFirstComment` says so; anywhere else it is refused, not dropped. */
  firstComment: z.string().trim().max(FIRST_COMMENT_MAX).nullish(),
  media: z.array(SocialMedia).max(35).default([]),
  surface: z.string().max(40).nullish(),
  languageCode: z.string().max(10).nullish(),
  aiGenerated: z.boolean().default(false),
  options: z.record(z.string(), z.unknown()).default({}),
  scheduledAt: IsoDateTime.nullish(),
  /** With sourceType, makes a repeated hand-over return the same post. */
  sourceType: z.string().max(40).nullish(),
  sourceId: z.string().max(200).nullish(),
});
export type SocialPostCreate = z.input<typeof SocialPostCreate>;

export const SocialPostDto = z.object({
  id: Uuid,
  channelId: Uuid,
  status: z.string(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  body: z.string(),
  hashtags: z.array(z.string()),
  linkUrl: z.string().nullable(),
  firstComment: z.string().nullable(),
  media: z.array(SocialMedia),
  surface: z.string().nullable(),
  languageCode: z.string().nullable(),
  aiGenerated: z.boolean(),
  scheduledAt: IsoDateTime.nullable(),
  contentHash: z.string(),
  attempts: z.number().int(),
  nextAttemptAt: IsoDateTime.nullable(),
  lastErrorCode: z.string().nullable(),
  lastError: z.string().nullable(),
  remoteId: z.string().nullable(),
  permalink: z.string().nullable(),
  publishedAt: IsoDateTime.nullable(),
  withdrawnAt: IsoDateTime.nullable(),
  /** No platform was touched: the live switch was off. */
  dryRun: z.boolean(),
  createdBy: z.string(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type SocialPostDto = z.infer<typeof SocialPostDto>;
export const SocialPostPage = pageOf(SocialPostDto);
export const SocialPostListQuery = IdPageQuery.extend({ status: SocialPostStatus.optional(), channelId: Uuid.optional() });

export const PublishAttemptDto = z.object({
  id: Uuid,
  attemptNo: z.number().int(),
  kind: z.string(),
  outcome: z.string(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  retryAfterMs: z.number().int().nullable(),
  note: z.string().nullable(),
  /** The links as they left, on an attempt that sent. */
  sentLinks: z.array(z.string()),
  actorId: z.string(),
  actorName: z.string(),
  createdAt: IsoDateTime,
});
export type PublishAttemptDto = z.infer<typeof PublishAttemptDto>;
export const PublishAttemptPage = pageOf(PublishAttemptDto);

export const PublishReceiptDto = z.object({
  id: Uuid,
  postId: Uuid,
  channelId: Uuid,
  remoteId: z.string().nullable(),
  permalink: z.string().nullable(),
  responseHash: z.string().nullable(),
  /** The links as they left: measured or short on a post from the queue. */
  sentLinks: z.array(z.string()),
  /** The first comment this post owed, in full, and what became of it. Null when it owed none. */
  firstCommentText: z.string().nullable(),
  firstCommentStatus: FirstCommentStatus.nullable(),
  firstCommentError: z.string().nullable(),
  dryRun: z.boolean(),
  source: z.string(),
  publishedAt: IsoDateTime,
  publishedBy: z.string(),
  withdrawnAt: IsoDateTime.nullable(),
  withdrawnBy: z.string().nullable(),
});
export type PublishReceiptDto = z.infer<typeof PublishReceiptDto>;
export const PublishReceiptPage = pageOf(PublishReceiptDto);
export const PublishReceiptListQuery = IdPageQuery.extend({ channelId: Uuid.optional(), postId: Uuid.optional() });

/** What a person found on the platform. "published" records it without sending; "not_published" may queue it again. */
export const ResolveReviewInput = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("published"), remoteId: z.string().trim().max(500).nullish(), permalink: z.string().url().max(2000).nullish(), note: z.string().max(2000).nullish() }),
  z.object({ outcome: z.literal("not_published"), retry: z.boolean().default(false), note: z.string().max(2000).nullish() }),
]);
export type ResolveReviewInput = z.input<typeof ResolveReviewInput>;

export const WithdrawInput = z.object({
  /** A person already took it down on the platform, so only the record changes. */
  byHand: z.boolean().default(false),
  note: z.string().max(2000).nullish(),
});

export const WithdrawResultDto = z.object({
  post: SocialPostDto,
  withdrawn: z.boolean(),
  /** Set when a person has to do it on the platform. */
  manualRequired: z.boolean(),
  reason: z.string().nullable(),
  dryRun: z.boolean(),
});
export type WithdrawResultDto = z.infer<typeof WithdrawResultDto>;

// ---------------------------------------------------------------------------
// Manual cards
// ---------------------------------------------------------------------------

export const ManualCardDto = z.object({
  postId: Uuid,
  channelId: Uuid,
  platform: z.string(),
  channelName: z.string(),
  status: z.string(),
  /** Ready to paste: body, then the hashtags not already in it. */
  copyText: z.string(),
  linkUrl: z.string().nullable(),
  /** To paste under the post once it is up: the first comment with the link in it. */
  firstComment: z.string().nullable(),
  media: z.array(SocialMedia),
  scheduledAt: IsoDateTime.nullable(),
  attempts: z.number().int(),
  reminders: z.number().int(),
  remindAt: IsoDateTime.nullable(),
  publishedAt: IsoDateTime.nullable(),
  publishedBy: z.string().nullable(),
  permalink: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type ManualCardDto = z.infer<typeof ManualCardDto>;
export const ManualCardPage = pageOf(ManualCardDto);
export const ManualCardListQuery = IdPageQuery.extend({ state: z.enum(["pending", "published", "all"]).default("pending") });

export const ManualAttemptInput = z.object({ note: z.string().trim().max(2000).nullish() });
export const MarkManualPublishedInput = z.object({
  permalink: z.string().url().max(2000).nullish(),
  remoteId: z.string().trim().max(500).nullish(),
  /** When it really went out, if not just now. */
  publishedAt: IsoDateTime.nullish(),
  note: z.string().trim().max(2000).nullish(),
});

// ---------------------------------------------------------------------------
// Notices
// ---------------------------------------------------------------------------

export const ChannelNoticeDto = z.object({
  id: Uuid,
  channelId: Uuid.nullable(),
  postId: Uuid.nullable(),
  code: z.string(),
  severity: NoticeSeverity,
  details: z.unknown(),
  firstSeenAt: IsoDateTime,
  lastSeenAt: IsoDateTime,
  ackedAt: IsoDateTime.nullable(),
  ackedBy: z.string().nullable(),
  resolvedAt: IsoDateTime.nullable(),
});
export type ChannelNoticeDto = z.infer<typeof ChannelNoticeDto>;
export const ChannelNoticePage = pageOf(ChannelNoticeDto);
export const ChannelNoticeListQuery = IdPageQuery.extend({ open: z.union([z.boolean(), z.enum(["true", "false"]).transform((value) => value === "true")]).default(true), channelId: Uuid.optional() });
